import { useGetMyProfile } from '../../../../hooks/queries/profile/use-get-my-profile'
import imgSignLeft from '../../modal-games/components/images/sign-left-blank.png'
import imgSignRight from '../../modal-games/components/images/sign-right-blank.png'

export type SignKey = 'rules' | 'rewards' | 'ranking' | 'history'

type Lang = 'pt' | 'en' | 'es' | 'ar' | 'fr' | 'de' | 'zh' | 'id' | 'tr'

const LABELS: Record<Lang, Record<SignKey, string>> = {
  pt: { rules: 'REGRAS', rewards: 'PRÊMIOS', ranking: 'RANKING', history: 'GANHOS' },
  en: { rules: 'RULES', rewards: 'REWARDS', ranking: 'RANKING', history: 'MY WINS' },
  es: { rules: 'REGLAS', rewards: 'PREMIOS', ranking: 'RANKING', history: 'GANANCIAS' },
  ar: { rules: 'القواعد', rewards: 'الجوائز', ranking: 'الترتيب', history: 'أرباحي' },
  fr: { rules: 'RÈGLES', rewards: 'PRIX', ranking: 'CLASSEMENT', history: 'GAINS' },
  de: { rules: 'REGELN', rewards: 'PREISE', ranking: 'RANGLISTE', history: 'GEWINNE' },
  zh: { rules: '规则', rewards: '奖励', ranking: '排行榜', history: '我的奖品' },
  id: { rules: 'ATURAN', rewards: 'HADIAH', ranking: 'PERINGKAT', history: 'KEMENANGAN' },
  tr: { rules: 'KURALLAR', rewards: 'ÖDÜLLER', ranking: 'SIRALAMA', history: 'KAZANÇLAR' },
}

// users.language guarda o PAÍS (br, us, dz, sa, id, tr...) → idioma da placa
const COUNTRY_LANG: Record<string, Lang> = {
  br: 'pt', pt: 'pt', ao: 'pt', mz: 'pt', cv: 'pt',
  es: 'es', mx: 'es', ar: 'es', co: 'es', cl: 'es', pe: 'es', ve: 'es', ec: 'es',
  bo: 'es', py: 'es', uy: 'es', cr: 'es', pa: 'es', do: 'es', gt: 'es', hn: 'es',
  sv: 'es', ni: 'es', cu: 'es',
  dz: 'ar', sa: 'ar', ae: 'ar', eg: 'ar', ma: 'ar', tn: 'ar', ly: 'ar', iq: 'ar',
  jo: 'ar', kw: 'ar', qa: 'ar', om: 'ar', bh: 'ar', lb: 'ar', sy: 'ar', ye: 'ar',
  sd: 'ar', ps: 'ar',
  fr: 'fr', be: 'fr', sn: 'fr', ci: 'fr',
  de: 'de', at: 'de',
  cn: 'zh', tw: 'zh', hk: 'zh', zh: 'zh',
  id: 'id',
  tr: 'tr',
}

function useSignLang(): Lang {
  const { user } = useGetMyProfile()
  const byCountry = COUNTRY_LANG[String(user?.language ?? '').toLowerCase()]
  if (byCountry) return byCountry
  const nav = (navigator.language || 'en').split('-')[0].toLowerCase()
  return (nav in LABELS ? nav : 'en') as Lang
}

// letra encolhe com o tamanho da palavra pra caber no corpo da placa
function fontSizeFor(text: string, lang: Lang) {
  if (lang === 'zh' || lang === 'ar') return 11
  const n = text.length
  if (n <= 5) return 10.5
  if (n <= 6) return 10
  if (n <= 7) return 9.2
  if (n <= 8) return 8.4
  if (n <= 9) return 7.6
  return 6.9
}

interface Props {
  sign: SignKey
  /** para onde a ponta da seta aponta */
  point: 'left' | 'right'
  onClick: () => void
}

/**
 * Placa de madeira do gold miner. Arte SEM texto (as originais tinham o texto
 * pintado em inglês); o rótulo vai por cima em HTML, no idioma do país do
 * jogador, centralizado no CORPO da placa (sem a ponta) e inclinado com a madeira.
 */
export function WoodSign({ sign, point, onClick }: Props) {
  const lang = useSignLang()
  const label = LABELS[lang][sign]
  const left = point === 'left'

  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="relative block h-10 transition active:scale-95"
    >
      <img
        src={left ? imgSignLeft : imgSignRight}
        alt=""
        className="pointer-events-none h-full w-auto object-contain"
      />
      <span
        className="absolute flex items-center justify-center leading-none font-black whitespace-nowrap text-[#3a342c]"
        style={{
          // corpo da placa: ~30%→94% (ponta à esquerda) ou 6%→70% (à direita)
          left: left ? '30%' : '6%',
          right: left ? '6%' : '30%',
          top: '22%',
          bottom: '14%',
          fontSize: fontSizeFor(label, lang),
          letterSpacing: lang === 'zh' || lang === 'ar' ? 0 : '0.04em',
          transform: `rotate(${left ? 4 : -4}deg)`,
          textShadow: '0 1px 0 rgba(255,235,200,0.55)',
          fontFamily: "'Arial Black', 'Helvetica Neue', Arial, sans-serif",
        }}
      >
        {label}
      </span>
    </button>
  )
}
